// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 29-app-environment-variables.md
//
// Simple text fields shared by IntegrationDialog's register and edit modes
// so both present the identical label, placeholder, and error styling
// (docs/28 §/integrations).
import * as React from 'react';
import { AlertTriangle } from 'lucide-react';

import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { connectedDbInjectedEnvNames } from '@greenlight/shared';

/** Whether a base URL is plain `http://` (docs/09 §Generic REST integration). */
export function isPlainHttpUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    return new URL(value.trim()).protocol === 'http:';
  } catch {
    return false;
  }
}

/** Marks an integration whose base URL is plain HTTP. */
export function NotEncryptedBadge(): React.JSX.Element {
  return (
    <StatusBadge variant="degraded" data-testid="not-encrypted-badge">
      Not encrypted
    </StatusBadge>
  );
}

export interface InsecureHttpWarningProps {
  acknowledged: boolean;
  onAcknowledgedChange: (value: boolean) => void;
  error?: string;
  /** The same-host https:// URL the reachability check saw a redirect to. */
  httpsSuggestion?: string | null;
  onUseHttps?: (url: string) => void;
}

/** The warning and confirming checkbox an `http://` base URL needs before Continue and Register. */
export function InsecureHttpWarning({
  acknowledged,
  onAcknowledgedChange,
  error,
  httpsSuggestion,
  onUseHttps,
}: InsecureHttpWarningProps): React.JSX.Element {
  return (
    <Alert variant="warning" data-testid="insecure-http-warning">
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>This base URL is not encrypted</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          Traffic between Greenlight and this upstream is plain HTTP. Anyone on the network path can
          read or change requests and responses, and can read the credential Greenlight sends. Use
          it only for an upstream that offers no HTTPS, preferably on a private network.
        </p>
        {httpsSuggestion && onUseHttps && (
          <div className="flex flex-wrap items-center gap-2" data-testid="https-suggestion">
            <span>
              The upstream redirects to <span className="font-mono">{httpsSuggestion}</span>.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onUseHttps(httpsSuggestion)}
            >
              Use the HTTPS URL
            </Button>
          </div>
        )}
        <div className="flex items-start gap-2 pt-1">
          <Checkbox
            id="allow-insecure-http"
            checked={acknowledged}
            onCheckedChange={(v) => onAcknowledgedChange(v === true)}
            className="mt-0.5"
            data-testid="allow-insecure-http"
          />
          <Label
            htmlFor="allow-insecure-http"
            className="cursor-pointer text-[12.5px] font-normal leading-snug text-foreground"
          >
            I understand that requests, responses, and the credential cross the network unencrypted.
          </Label>
        </div>
        {error && <div className="text-[11.5px] text-destructive">{error}</div>}
      </AlertDescription>
    </Alert>
  );
}

export interface BaseUrlFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  /** Shows **Not encrypted** beside the label for a plain-HTTP URL. */
  markPlainHttp?: boolean;
}

/** The proxy-target base URL for an HTTP integration. */
export function BaseUrlField({
  id,
  value,
  onChange,
  error,
  markPlainHttp = false,
}: BaseUrlFieldProps): React.JSX.Element {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label htmlFor={id}>Base URL</Label>
        {markPlainHttp && isPlainHttpUrl(value) && <NotEncryptedBadge />}
      </div>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://api.example.com"
      />
      {error && <p className="text-[11.5px] text-destructive">{error}</p>}
    </div>
  );
}

export interface DatabaseNameFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

/** The connected-database name (ADR-0030), alongside {@link RegisterConnectivityStep}'s host/port. */
export function DatabaseNameField({
  id,
  value,
  onChange,
  error,
}: DatabaseNameFieldProps): React.JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Database name</Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. cast"
        className="font-mono"
      />
      {error && <p className="text-[11.5px] text-destructive">{error}</p>}
    </div>
  );
}

export interface EnvVarNameFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  isDatabase: boolean;
  error?: string;
}

/**
 * The env var an injected credential is bound under (docs/29 § Reserved Names).
 * For a connected database it is a prefix, and the field lists the names it
 * expands to.
 */
export function EnvVarNameField({
  id,
  value,
  onChange,
  isDatabase,
  error,
}: EnvVarNameFieldProps): React.JSX.Element {
  const prefix = value.trim() || 'PREFIX';
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{isDatabase ? 'Env var prefix' : 'Env var name'}</Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder={isDatabase ? 'e.g. REPORTING_DB' : 'e.g. HUBSPOT_API_KEY'}
        className="font-mono"
        autoCapitalize="characters"
      />
      {error ? (
        <p className="text-[11.5px] text-destructive">{error}</p>
      ) : isDatabase ? (
        <p className="text-[11.5px] text-muted-foreground" data-testid="env-var-prefix-names">
          The app receives{' '}
          {Object.values(connectedDbInjectedEnvNames(prefix)).map((name, i, all) => (
            <React.Fragment key={name}>
              <code className="font-mono">{name}</code>
              {i < all.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}
          . None may collide with a reserved managed name.
        </p>
      ) : (
        <p className="text-[11.5px] text-muted-foreground">
          The bound credential is injected under this name. Must not collide with a reserved managed
          name.
        </p>
      )}
    </div>
  );
}
